import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Loader2, Check } from "lucide-react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { getCardDesign, TOTAL_CARD_VARIANTS, type CardVariant } from "@/lib/card-designs";

const cardVariants = Array.from({ length: TOTAL_CARD_VARIANTS }, (_, i) => (i + 1) as CardVariant);

interface Customer {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

interface Product {
  id: string;
  name: string;
  cardType: string;
  cardNetwork: string;
}

interface IssueCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  preselectedCustomerId?: string;
  productId?: string; // Pre-fill when creating from product detail page
}

export function IssueCardDialog({
  open,
  onOpenChange,
  onSuccess,
  preselectedCustomerId,
  productId: propProductId,
}: IssueCardDialogProps) {
  const { toast } = useToast();
  const { baseCurrency } = useOrgSettings();

  const [formData, setFormData] = useState({
    customerId: preselectedCustomerId || "",
    productId: propProductId || "",
    nickname: "",
    dailyLimitCents: 50000,
    monthlyLimitCents: 200000,
    cardDesign: 1 as number,
  });

  const { data: customers } = useQuery<Customer[]>({
    queryKey: ["customers"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/customers");
      if (!response.ok) throw new Error("Failed to fetch customers");
      return response.json();
    },
    enabled: open && !preselectedCustomerId,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/products");
      if (!response.ok) throw new Error("Failed to fetch products");
      return response.json();
    },
    enabled: open,
  });

  const issueMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/cards", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to issue card");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["product-cards"] });
      toast({
        title: "Success",
        description: "Card issued successfully",
      });
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      customerId: preselectedCustomerId || "",
      productId: propProductId || "",
      nickname: "",
      dailyLimitCents: 50000,
      monthlyLimitCents: 200000,
      cardDesign: 1,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    issueMutation.mutate(formData);
  };

  const selectedProduct = products?.find((p) => p.id === formData.productId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Issue New Card</DialogTitle>
          <DialogDescription>
            Issue a new virtual card to a customer
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {!preselectedCustomerId && (
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select
                  value={formData.customerId}
                  onValueChange={(v) =>
                    setFormData({ ...formData, customerId: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.firstName} {c.lastName} ({c.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Card Product *</Label>
              <Select
                value={formData.productId}
                onValueChange={(v) =>
                  setFormData({ ...formData, productId: v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Product" />
                </SelectTrigger>
                <SelectContent>
                  {products?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.cardType} - {p.cardNetwork})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedProduct && (
                <p className="text-xs text-muted-foreground">
                  {selectedProduct.cardType} card on {selectedProduct.cardNetwork} network
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Card Nickname</Label>
              <Input
                value={formData.nickname}
                onChange={(e) =>
                  setFormData({ ...formData, nickname: e.target.value })
                }
                placeholder="e.g., Travel Card, Office Supplies"
              />
            </div>

            <div className="space-y-2">
              <Label>Card Design</Label>
              <div className="grid grid-cols-3 gap-2">
                {cardVariants.map((variant) => {
                  const design = getCardDesign(variant);
                  const isSelected = formData.cardDesign === variant;
                  return (
                    <button
                      key={variant}
                      type="button"
                      onClick={() => setFormData({ ...formData, cardDesign: variant })}
                      className={cn(
                        "relative rounded-lg overflow-hidden transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        "aspect-[1.6/1]",
                        isSelected
                          ? "ring-2 ring-primary ring-offset-2 shadow-md"
                          : "ring-1 ring-border/50 opacity-60 hover:opacity-100 hover:ring-border"
                      )}
                    >
                      <img
                        src={design.front}
                        alt={`Design ${variant}`}
                        className="w-full h-full object-cover"
                      />
                      {isSelected && (
                        <div className="absolute top-1 right-1 h-4 w-4 rounded-full bg-primary flex items-center justify-center shadow-sm">
                          <Check className="h-2.5 w-2.5 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Daily Limit ({baseCurrency})</Label>
                <Input
                  type="number"
                  value={formData.dailyLimitCents / 100}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      dailyLimitCents: parseFloat(e.target.value) * 100,
                    })
                  }
                  min={0}
                  step={10}
                />
              </div>
              <div className="space-y-2">
                <Label>Monthly Limit ({baseCurrency})</Label>
                <Input
                  type="number"
                  value={formData.monthlyLimitCents / 100}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      monthlyLimitCents: parseFloat(e.target.value) * 100,
                    })
                  }
                  min={0}
                  step={100}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={issueMutation.isPending || !formData.customerId || !formData.productId}
            >
              {issueMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Issue Card
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
